import React, { useEffect } from 'react'
import { Route, Switch, useRouteMatch } from 'react-router'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import { AccountProvider, useCustomer, useNavigation } from 'msp-integrations'
import {
  AccountData,
  AccountDataSharing,
  MainWrapper,
  Spinner
} from 'msp-components'
import PageLayout from '@/components/cms/page-layout'
import Seo from '@/components/seo'

import AccountLogout from './AccountLogout/AccountLogout'
import AccountHeader from './AccountHeader/AccountHeader'
import AccountMenu from './AccountMenu/AccountMenu'
import AccountOverview from './AccountOverview/AccountOverview'
import AccountAddresses from './AccountAddresses/AccountAddresses'
import AccountPayments from './AccountPayments/AccountPayments'
import AccountOrders from './AccountOrders/AccountOrders'
import AccountMachines from './AccountMachines/AccountMachines'
import AccountWishlist from './AccountWishlist/AccountWishlist'

import * as S from './Account.styles'

const Account = ({ page }: any) => {
  const { path } = useRouteMatch()
  const { formatMessage } = useIntl()
  const location = useLocation()
  const navigate = useNavigation()
  const customer = useCustomer()

  // If we have customer data and they are not registered, push to login page
  // Using Redirect allows us to store the directed page to location
  // so we can direct users back after they are successfully log in
  useEffect(() => {
    if (customer.authType != null && !customer.isRegistered) {
      navigate(`/login?redirectUrl=${location.pathname}`)
    }
  }, [customer])

  const scrollToTop = () => {
    const node = document.getElementById('account-top-scroll-anchor')
    node.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
  }

  return customer?.isInitialized && customer?.isRegistered ? (
    <AccountProvider>
      <div
        className={S.AccountMainWrapper}
        data-testid={
          customer.isRegistered ? 'account-page' : 'account-page-skeleton'
        }
      >
        <MainWrapper>
          <div className={S.AccountRowItem}>
            <div
              className={`${S.AccountLeftColumn} ${S.AccountLeftColumnHeader}`}
            >
              <AccountLogout customer={customer} />
            </div>
            <div
              className={`${S.AccountRightColumn} lg:py-16`}
              id='account-top-scroll-anchor'
            >
              <AccountHeader customer={customer} />
            </div>
          </div>
          <div className={S.AccountRowItem}>
            <div
              className={`${S.AccountLeftColumn} ${S.AccountLeftColumnMenu}`}
            >
              <AccountMenu />
            </div>
            <div className={S.AccountRightColumn}>
              <Switch>
                <Route exact path={path}>
                  <Seo
                    title={
                      page.found && page?.meta?.title
                        ? page?.meta?.title
                        : formatMessage({
                            id: 'msp.account_overview.seo.title',
                            defaultMessage: 'Account Overview'
                          })
                    }
                    description={
                      page.found && page?.meta?.description
                        ? page?.meta?.description
                        : formatMessage({
                            id: 'msp.account_overview.seo.description',
                            defaultMessage: 'The account overview page.'
                          })
                    }
                    keywords={
                      page.found && page?.meta?.keywords
                        ? page?.meta?.keywords
                        : ''
                    }
                    canonicalPath={'/account'}
                  />
                  <AccountOverview />
                </Route>
                <Route path={`${path}/orders`}>
                  <Seo
                    title={
                      page.found && page?.meta?.title
                        ? page?.meta?.title
                        : formatMessage({
                            id: 'msp.account_orders.seo.title',
                            defaultMessage: 'Account Orders'
                          })
                    }
                    description={
                      page.found && page?.meta?.description
                        ? page?.meta?.description
                        : formatMessage({
                            id: 'msp.account_orders.seo.description',
                            defaultMessage: 'The account orders page.'
                          })
                    }
                    keywords={
                      page.found && page?.meta?.keywords
                        ? page?.meta?.keywords
                        : ''
                    }
                    canonicalPath={'/account/orders'}
                  />
                  <AccountOrders />
                </Route>
                <Route exact path={`${path}/wishlist`}>
                  <Seo
                    title={
                      page.found && page?.meta?.title
                        ? page?.meta?.title
                        : formatMessage({
                            id: 'msp.account_wishlist.seo.title',
                            defaultMessage: 'Account Wishlist'
                          })
                    }
                    description={
                      page.found && page?.meta?.description
                        ? page?.meta?.description
                        : formatMessage({
                            id: 'msp.account_wishlist.seo.description',
                            defaultMessage: 'The account wishlist page.'
                          })
                    }
                    keywords={
                      page.found && page?.meta?.keywords
                        ? page?.meta?.keywords
                        : ''
                    }
                    canonicalPath={'/account/wishlist'}
                  />
                  <AccountWishlist />
                </Route>
                <Route exact path={`${path}/my-data`}>
                  <Seo
                    title={
                      page.found && page?.meta?.title
                        ? page?.meta?.title
                        : formatMessage({
                            id: 'msp.account_details.seo.title',
                            defaultMessage: 'Account Details'
                          })
                    }
                    description={
                      page.found && page?.meta?.description
                        ? page?.meta?.description
                        : formatMessage({
                            id: 'msp.account_details.seo.description',
                            defaultMessage: 'The account details page.'
                          })
                    }
                    keywords={
                      page.found && page?.meta?.keywords
                        ? page?.meta?.keywords
                        : ''
                    }
                    canonicalPath={'/account/my-data'}
                  />
                  <AccountData />
                </Route>
                <Route exact path={`${path}/addresses`}>
                  <Seo
                    title={
                      page.found && page?.meta?.title
                        ? page?.meta?.title
                        : formatMessage({
                            id: 'msp.account_addresses.seo.title',
                            defaultMessage: 'Account Addresses'
                          })
                    }
                    description={
                      page.found && page?.meta?.description
                        ? page?.meta?.description
                        : formatMessage({
                            id: 'msp.account_addresses.seo.description',
                            defaultMessage: 'The account addresses page.'
                          })
                    }
                    keywords={
                      page.found && page?.meta?.keywords
                        ? page?.meta?.keywords
                        : ''
                    }
                    canonicalPath={'/account/addresses'}
                  />
                  <AccountAddresses scrollTop={scrollToTop} />
                </Route>
                <Route exact path={`${path}/payment-methods`}>
                  <Seo
                    title={
                      page.found && page?.meta?.title
                        ? page?.meta?.title
                        : formatMessage({
                            id: 'msp.account_payments.seo.title',
                            defaultMessage: 'Account Payment Methods'
                          })
                    }
                    description={
                      page.found && page?.meta?.description
                        ? page?.meta?.description
                        : formatMessage({
                            id: 'msp.account_payments.seo.description',
                            defaultMessage: 'The account payment methods page.'
                          })
                    }
                    keywords={
                      page.found && page?.meta?.keywords
                        ? page?.meta?.keywords
                        : ''
                    }
                    canonicalPath={'/account/payment-methods'}
                  />
                  <AccountPayments scrollTop={scrollToTop} />
                </Route>
                <Route exact path={`${path}/data-sharing`}>
                  <Seo
                    title={
                      page.found && page?.meta?.title
                        ? page?.meta?.title
                        : formatMessage({
                            id: 'msp.account_privacy.seo.title',
                            defaultMessage: 'Account Data Sharing'
                          })
                    }
                    description={
                      page.found && page?.meta?.description
                        ? page?.meta?.description
                        : formatMessage({
                            id: 'msp.account_privacy.seo.description',
                            defaultMessage: 'The account data sharing page.'
                          })
                    }
                    keywords={
                      page.found && page?.meta?.keywords
                        ? page?.meta?.keywords
                        : ''
                    }
                    canonicalPath={'/account/data-sharing'}
                  />
                  <AccountDataSharing customer={customer} />
                </Route>
                <Route exact path={`${path}/my-machines`}>
                  <Seo
                    title={
                      page.found && page?.meta?.title
                        ? page?.meta?.title
                        : formatMessage({
                            id: 'msp.account_machines.seo.title',
                            defaultMessage: 'Account Machines'
                          })
                    }
                    description={
                      page.found && page?.meta?.description
                        ? page?.meta?.description
                        : formatMessage({
                            id: 'msp.account_machines.seo.description',
                            defaultMessage: 'The account machines page.'
                          })
                    }
                    keywords={
                      page.found && page?.meta?.keywords
                        ? page?.meta?.keywords
                        : ''
                    }
                    canonicalPath={'/account/my-machines'}
                  />
                  <AccountMachines />
                </Route>
              </Switch>
            </div>
          </div>
        </MainWrapper>
      </div>
    </AccountProvider>
  ) : (
    <Spinner fullScreen />
  )
}

const AccountWrapper = (props) => {
  return <PageLayout {...Object.assign({ ChildPage: Account }, props)} />
}

export default AccountWrapper
